import { updateChatState, emit, on, clearMessages, getState, clearLocalStorage } from "../../core/state.js";
import { getTheme, getTenantId, getUiFeature, getFeature } from "../../core/configAccessors.js";
import { endSession } from "../../core/session.js";
import { syncCartWithWebsite } from "../../features/optional/cart.js";
import { CLUB_SESSION_KEY } from "../../features/optional/club_c7.js";

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

const ICON_REFRESH = `<svg viewBox="0 0 17 17" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 8.5a7.5 7.5 0 1 1-2.2-5.3L16 5.2M16 5.2V1M16 5.2h-4.2"/></svg>`;
const ICON_MINIMIZE = `<svg viewBox="0 0 14 2" width="14" height="2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M1 1h12.7"/></svg>`;
const ICON_CLOSE = `<svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M11 1L1 11M1 1l10 10"/></svg>`;
// Old Fogarty header icons (script.js 278-296). Login = person on a filled
// white circle; cart = gold-stroked basket. Kept verbatim for visual parity.
const ICON_USER = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="white"/><path d="M10 10.5C11.3807 10.5 12.5 9.38071 12.5 8C12.5 6.61929 11.3807 5.5 10 5.5C8.61929 5.5 7.5 6.61929 7.5 8C7.5 9.38071 8.61929 10.5 10 10.5ZM10 10.5C11.0609 10.5 12.0783 10.9214 12.8284 11.6716C13.5786 12.4217 14 13.4391 14 14.5M10 10.5C8.93913 10.5 7.92172 10.9214 7.17157 11.6716C6.42143 12.4217 6 13.4391 6 14.5" stroke="black" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
const ICON_CART = `<svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1H2.66667L4.88333 11.35C4.96465 11.7291 5.17556 12.0679 5.47976 12.3082C5.78396 12.5485 6.16242 12.6753 6.55 12.6667H14.7C15.0793 12.6661 15.4471 12.5361 15.7425 12.2982C16.038 12.0603 16.2434 11.7288 16.325 11.3583L17.7 5.16667M6.79183 16.7916C6.79183 17.2519 6.41873 17.625 5.9585 17.625C5.49826 17.625 5.12516 17.2519 5.12516 16.7916C5.12516 16.3314 5.49826 15.9583 5.9585 15.9583C6.41873 15.9583 6.79183 16.3314 6.79183 16.7916ZM15.9585 16.7916C15.9585 17.2519 15.5854 17.625 15.1252 17.625C14.6649 17.625 14.2918 17.2519 14.2918 16.7916C14.2918 16.3314 14.6649 15.9583 15.1252 15.9583C15.5854 15.9583 15.9585 16.3314 15.9585 16.7916Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

export default class Header {
  render() {
    const header = document.createElement("div");
    header.className = "ws-header";

    const logoDiv = document.createElement("div");
    logoDiv.className = "ws-header-logo";

    const headerIconUrl = getTheme("header_icon_url", null);
    const botName = getTheme("bot_name", "Concierge");

    if (headerIconUrl) {
      const img = document.createElement("img");
      img.src = headerIconUrl;
      img.alt = botName;
      logoDiv.appendChild(img);
    } else {
      const span = document.createElement("span");
      span.textContent = botName;
      logoDiv.appendChild(span);
    }

    const actionsDiv = document.createElement("div");
    actionsDiv.className = "ws-header-actions";

    // Header account control, gated by config flags (default off, so other
    // tenants are unaffected). Rendered to the left of the standard
    // reload/minimize/close, matching the old Fogarty layout.
    //
    // Every tenant with an account button (Fogarty/Wine Engine on Shopify,
    // Lightning Rock + Potter's Vineyard + Valle della Pace on Commerce7)
    // gets the common login dropdown: Shopify keeps its external storefront
    // links, Commerce7 tenants get an in-chat Login (the dropdown triggers
    // the same __login__ prompt account.js's chip already handles).
    if (getUiFeature("show_user_account", false) || getUiFeature("show_cart", false)) {
      actionsDiv.appendChild(this._buildLogin());
    }
    if (getUiFeature("show_cart", false)) {
      actionsDiv.appendChild(this._buildCart());
    }

    const refreshBtn = document.createElement("button");
    refreshBtn.className = "ws-btn-refresh";
    refreshBtn.title = "Refresh";
    refreshBtn.innerHTML = ICON_REFRESH;
    // Soft reset — same as old reloadChat(): overlay spinner + clear transcript
    // + re-seed welcome (session kept). Common for all wineries.
    refreshBtn.addEventListener("click", () => emit("ws-chat-reload"));

    const minimizeBtn = document.createElement("button");
    minimizeBtn.className = "ws-btn-minimize";
    minimizeBtn.type = "button";
    minimizeBtn.title = "Minimize";
    minimizeBtn.innerHTML = ICON_MINIMIZE;
    minimizeBtn.addEventListener("click", () => {
      updateChatState({ isOpen: false });
      emit("ws-toggle", { isOpen: false });
    });

    const closeBtn = document.createElement("button");
    closeBtn.className = "ws-btn-close";
    closeBtn.title = "Close";
    closeBtn.innerHTML = ICON_CLOSE;
    // Close ends the conversation: clear transcript + end session, then hide.
    // Next open starts a fresh session.
    closeBtn.addEventListener("click", () => {
      clearMessages();
      clearLocalStorage([CLUB_SESSION_KEY(getTenantId())]);
      endSession(getTenantId());
      updateChatState({ isOpen: false });
      emit("ws-toggle", { isOpen: false });
      syncCartWithWebsite();
    });

    actionsDiv.appendChild(refreshBtn);
    actionsDiv.appendChild(minimizeBtn);
    actionsDiv.appendChild(closeBtn);

    header.appendChild(logoDiv);
    header.appendChild(actionsDiv);

    return header;
  }

  // Header account control: person icon + a dropdown. Register always links
  // to the storefront URL from tenant config. Login does too for Shopify
  // tenants; for Commerce7 tenants it instead triggers the in-chat login
  // form (same __login__ prompt account.js's logged-out chip already
  // handles) — the widget has no page to navigate to for that.
  _buildLogin() {
    const wrap = document.createElement("div");
    wrap.className = "ws-login";

    const btn = document.createElement("button");
    btn.className = "ws-btn-login";
    btn.title = "Account";
    btn.innerHTML = ICON_USER;

    const menu = document.createElement("div");
    menu.className = "ws-login-menu";
    menu.hidden = true;

    const isCommerce7 = isTrue(getFeature("commerce7"));

    // Commerce7 tenants: the menu depends on login state (customerToken),
    // which changes after the in-chat login form succeeds or Logout is
    // clicked — rebuild it on those events instead of leaving it stuck on
    // whatever it showed at header render time.
    const renderMenu = () => {
      menu.replaceChildren();

      const addLink = (label, href) => {
        if (!href) return;
        const a = document.createElement("a");
        a.className = "ws-login-item";
        a.href = href;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.textContent = label;
        menu.appendChild(a);
      };
      const addAction = (label, onClick) => {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "ws-login-item";
        item.textContent = label;
        item.addEventListener("click", onClick);
        menu.appendChild(item);
      };

      if (isCommerce7) {
        const profile = getState().user?.profile;
        if (getState().user?.customerToken) {
          // Old login-dropdown: a non-interactive name row + divider above
          // Logout (script.js ~886-899).
          const first = profile?.firstName || profile?.first_name || "";
          const last = profile?.lastName || profile?.last_name || "";
          if (first || last) {
            const name = document.createElement("div");
            name.className = "ws-login-item ws-login-name";
            name.textContent = [first, last].filter(Boolean).join(" ");
            menu.appendChild(name);
            const hr = document.createElement("hr");
            hr.className = "ws-login-divider";
            menu.appendChild(hr);
          }
          addAction("Logout", () => {
            menu.hidden = true;
            emit("ws-prompt-select", { value: "__logout__" });
          });
        } else {
          addAction("Login", () => {
            menu.hidden = true;
            emit("ws-prompt-select", { value: "__login__" });
          });
          addLink("Register", getTheme("account_register_url", null));
        }
      } else {
        addLink("Login", getTheme("account_login_url", null));
        addLink("Register", getTheme("account_register_url", null));
      }
      if (!menu.childElementCount) {
        const span = document.createElement("span");
        span.className = "ws-login-item ws-login-item--empty";
        span.textContent = "Account unavailable";
        menu.appendChild(span);
      }
    };

    // Old login-option-icon: a colored circle with the first letter of the
    // customer's first name, replacing the generic person icon once logged in
    // (script.js ~886: background-color/width/height/font-size/grid-center).
    const renderButton = () => {
      const first = getState().user?.profile?.firstName || getState().user?.profile?.first_name || "";
      if (isCommerce7 && getState().user?.customerToken && first) {
        btn.innerHTML = "";
        const avatar = document.createElement("span");
        avatar.className = "ws-btn-avatar";
        avatar.textContent = first.charAt(0).toUpperCase();
        btn.appendChild(avatar);
      } else {
        btn.innerHTML = ICON_USER;
      }
    };

    renderMenu();
    renderButton();
    if (isCommerce7) {
      on("ws-login-success", () => {
        renderMenu();
        renderButton();
      });
      on("ws-logout-success", () => {
        renderMenu();
        renderButton();
      });
      // Old parity: getUserOptions()/loadUserLogin() re-read the customerToken
      // cookie fresh on every chat toggle-open (main.js ~1871), rather than
      // caching the icon. A page reload restores the token + shows the "Welcome
      // back" greeting via greetIfLoggedInC7 before this button's own
      // ws-login-success fires (or on a slow/older bundle where it doesn't at
      // all) — re-checking on open is the same old belt-and-suspenders fix.
      on("ws-toggle", ({ isOpen } = {}) => {
        if (!isOpen) return;
        renderMenu();
        renderButton();
      });
    }

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      menu.hidden = !menu.hidden;
    });
    // Dismiss when clicking elsewhere (clicks inside the shadow root compose).
    document.addEventListener("click", () => {
      menu.hidden = true;
    });

    wrap.appendChild(btn);
    wrap.appendChild(menu);
    return wrap;
  }

  // Header cart control: icon + count badge. The icon only reflects the count
  // (driven by ws-cart-count) and asks to open the cart (ws-cart-open). The
  // BE-backed cart feature owns fetching/rendering the contents.
  _buildCart() {
    const btn = document.createElement("button");
    btn.className = "ws-btn-cart";
    btn.title = "Cart";
    btn.innerHTML = ICON_CART;

    // Parity: the count is always shown (including 0), like the old
    // #shop-cart-total-items, centered in the basket and updated live.
    const badge = document.createElement("span");
    badge.className = "ws-cart-badge";
    badge.textContent = "0";
    btn.appendChild(badge);

    on("ws-cart-count", ({ count } = {}) => {
      badge.textContent = String(Number(count) || 0);
    });
    btn.addEventListener("click", () => emit("ws-cart-open"));
    return btn;
  }
}
